import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, statusVariant } from '../ui';
import type {
  ContentItem,
  ContentOutline,
  ContentMedia,
  ContentPillar,
  ContentPlan,
  ScheduledTask,
  Workspace,
} from '../../types';
import { MediaLightboxModal } from './MediaLightboxModal';
import { downloadContentItemMedia } from '../../api';
import './ContentDetailWorkspace.css';

type Tab = 'preview' | 'edit' | 'meta';

const TABS: { id: Tab; label: string }[] = [
  { id: 'preview', label: '👁️ Preview' },
  { id: 'edit', label: '✏️ Edit' },
  { id: 'meta', label: '📋 Meta & Workflow' },
];

type IconName =
  | 'chevronLeft'
  | 'chevronRight'
  | 'refresh'
  | 'refreshAll'
  | 'upload'
  | 'grid'
  | 'single'
  | 'hide'
  | 'show'
  | 'generate';

function Icon({ name }: { name: IconName }) {
  switch (name) {
    case 'chevronLeft':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      );
    case 'chevronRight':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      );
    case 'refresh':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      );
    case 'refreshAll':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 12a8 8 0 0 1 13.66-5.66" />
          <path d="M4 4v6h6" />
          <path d="M20 12a8 8 0 0 1-13.66 5.66" />
          <path d="M20 20v-6h-6" />
        </svg>
      );
    case 'upload':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 16V4" />
          <path d="M8 8l4-4 4 4" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'grid':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="4" width="6" height="6" rx="1" />
          <rect x="14" y="4" width="6" height="6" rx="1" />
          <rect x="4" y="14" width="6" height="6" rx="1" />
          <rect x="14" y="14" width="6" height="6" rx="1" />
        </svg>
      );
    case 'single':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <path d="M8 15l3-3 3 3 2-2 2 2" />
        </svg>
      );
    case 'hide':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
          <path d="M3 3l18 18" />
        </svg>
      );
    case 'show':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'generate':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
          <path d="M19 2l.9 2.7L22 5.6l-2.1.9L19 9l-.9-2.7L16 5.6l2.1-.9z" />
        </svg>
      );
    default:
      return null;
  }
}

export type ContentDetailWorkspaceProps = {
  outline: ContentOutline;
  contentItem: ContentItem | null;
  pillars: ContentPillar[];
  plans: ContentPlan[];
  allowedFormats: string[];
  initialTab?: Tab;

  // Media carousel
  currentMediaIndex: number;
  onPrevMedia: () => void;
  onNextMedia: () => void;
  onSetMediaIndex: (_idx: number) => void;

  // Pipeline actions
  generatingContent: boolean;
  /** Media IDs with an in-flight per-slide regeneration (allows multiple concurrent jobs). */
  generatingMediaIds: Set<number>;
  /** True while a multi-slide job (initial generate-all OR regenerate-all) is running. */
  multiSlideJobInFlight: boolean;
  /** True when the current multi-slide job is specifically a "re-render all" (vs initial gen). */
  regeneratingAllMedia: boolean;
  onGenerateContent: () => void;
  onRegenerateContent: () => void;
  onGenerateMedia: (_options?: {
    mediaIndex?: number;
    mediaId?: number;
    regenerateAll?: boolean;
  }) => void;
  onApprove: () => void;
  onApproveContentItem: () => void;
  onOpenPublishModal: () => void;

  // Caption editing
  editingCaption: boolean;
  captionDraft: string;
  savingCaption: boolean;
  onStartEditCaption: () => void;
  onCaptionDraftChange: (_val: string) => void;
  onSaveCaption: () => void;
  onCancelEditCaption: () => void;

  // Hashtag editing
  editingHashtags: boolean;
  hashtagsDraft: string;
  savingHashtags: boolean;
  onStartEditHashtags: () => void;
  onHashtagsDraftChange: (_val: string) => void;
  onSaveHashtags: () => void;
  onCancelEditHashtags: () => void;

  // Prompt editing
  editingPromptId: number | null;
  promptDraft: string;
  savingPrompt: boolean;
  onStartEditPrompt: (_mediaId: number, _prompt: string) => void;
  onPromptDraftChange: (_val: string) => void;
  onSavePrompt: (_mediaId: number) => void;
  onCancelEditPrompt: () => void;

  // Outline metadata editing (title/description/format/publish date)
  savingOutlineMeta: boolean;
  onSaveOutlineMeta: (_patch: {
    title: string;
    summary: string;
    content_format: string;
    publish_at?: string | null;
  }) => Promise<void>;

  // Media management (hide, delete, add, reorder, upload)
  onToggleHideMedia: (_mediaId: number, _hidden: boolean) => void;
  onDeleteMedia: (_mediaId: number) => void;
  onAddMediaPrompt: (_prompt: string) => void;
  onReorderMedia: (_orderedIds: number[]) => void;
  onUploadMediaFile: (_file: File, _mediaId?: number) => void;

  // Delete outline
  confirmDelete: boolean;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;

  // Instagram analytics
  refreshingInstagramMetrics?: boolean;
  onRefreshInstagramMetrics?: () => void;

  // Workflow info
  schedulerTasks?: ScheduledTask[];

  // Helpers
  formatDate: (_val?: string) => string;
  getPillarName: (_id?: number | null) => string;
  getPlanName: (_id?: number | null) => string;

  // Workspace CTA / sign-off image — used to show & toggle the closing slide
  workspace: Workspace;
  onToggleCtaImage: (_value: boolean | null) => void;
  togglingCta?: boolean;
  onGoToCtaSettings?: () => void;
};

// ── Internal sub-components ────────────────────────────────────────────────

function MediaFrame({
  contentItem,
  outline,
  currentMediaIndex,
  onPrevMedia,
  onNextMedia,
  onSetMediaIndex,
  generatingContent,
  generatingMediaIds,
  multiSlideJobInFlight,
  regeneratingAllMedia,
  onGenerateContent,
  onGenerateMedia,
  onToggleHideMedia,
  onUploadMediaFile,
}: Pick<
  ContentDetailWorkspaceProps,
  | 'contentItem'
  | 'outline'
  | 'currentMediaIndex'
  | 'onPrevMedia'
  | 'onNextMedia'
  | 'onSetMediaIndex'
  | 'generatingContent'
  | 'generatingMediaIds'
  | 'multiSlideJobInFlight'
  | 'regeneratingAllMedia'
  | 'onGenerateContent'
  | 'onGenerateMedia'
  | 'onToggleHideMedia'
  | 'onUploadMediaFile'
>) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [mediaViewMode, setMediaViewMode] = useState<'single' | 'grid'>('single');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasMedia = Boolean(contentItem?.media && contentItem.media.length > 0);
  const currentMedia: ContentMedia | null = hasMedia
    ? (contentItem!.media![currentMediaIndex] ?? null)
    : null;
  const isVideoFormat =
    contentItem?.content_format === 'video' || outline.content_format === 'video';
  const mediaCount = contentItem?.media?.length ?? 0;
  const isCarousel =
    mediaCount > 1 &&
    (contentItem?.content_format === 'carousel' || outline.content_format === 'carousel');
  const isCurrentMediaRegenerating = Boolean(
    currentMedia?.id !== undefined && generatingMediaIds.has(currentMedia.id)
  );
  // Disable per-slide buttons only when *that* slide is in flight or a multi-slide
  // job is running. Other slides remain freely clickable for concurrent jobs.
  const isCurrentSlideBusy = isCurrentMediaRegenerating || multiSlideJobInFlight;
  // Disable multi-slide buttons whenever any per-slide job OR multi-slide job is running.
  const isMultiSlideBusy = multiSlideJobInFlight || generatingMediaIds.size > 0;
  const currentMediaPrompt = currentMedia?.prompt?.trim();

  const openLightbox = (mediaIndex: number) => {
    const targetMedia = contentItem?.media?.[mediaIndex];
    if (!targetMedia?.media_url || targetMedia.media_type === 'video') {
      return;
    }
    setLightboxIndex(mediaIndex);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/') && contentItem) {
      onUploadMediaFile(file, currentMedia?.id);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && contentItem) {
      onUploadMediaFile(file, currentMedia?.id);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  if (!contentItem) {
    return (
      <div className="cdw-media-frame" style={{ minHeight: 240 }}>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ color: 'var(--color-neutral-400)', marginBottom: 16 }}>
            No content generated yet
          </p>
          <Button variant="primary" onClick={onGenerateContent} disabled={generatingContent}>
            {generatingContent ? 'Generating…' : '✨ Generate Content'}
          </Button>
        </div>
      </div>
    );
  }

  if (!hasMedia) {
    return (
      <div
        className={`cdw-media-frame cdw-media-frame--drop-zone${isDragOver ? ' cdw-media-frame--drag-over' : ''}`}
        style={{ minHeight: 240 }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <div style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ color: 'var(--color-neutral-400)', marginBottom: 16 }}>
            {isDragOver
              ? '📸 Drop to upload image'
              : isVideoFormat
                ? 'Video prompt ready'
                : 'Media prompts ready — generate media or drop an image here'}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => onGenerateMedia()} disabled={isMultiSlideBusy}>
              {multiSlideJobInFlight
                ? 'Generating…'
                : isVideoFormat
                  ? '🎬 Generate Video'
                  : isCarousel
                    ? '✨ Generate All Slide Images'
                    : '✨ Generate Image'}
            </Button>
            {!isVideoFormat && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isMultiSlideBusy}
                title="Upload an image from your device"
              >
                📁 Upload Image
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`cdw-media-frame${isDragOver ? ' cdw-media-frame--drag-over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Hidden slide overlay */}
      {currentMedia?.is_hidden && (
        <div className="cdw-media-hidden-overlay">
          <span>🚫 Hidden from post</span>
        </div>
      )}

      {/* Drop overlay */}
      {isDragOver && (
        <div className="cdw-media-drop-overlay">
          <span>📸 Drop to replace image</span>
        </div>
      )}

      {/* Top overlay bar — keeps toggle and action buttons in the same flex row to prevent overlap */}
      {(isCarousel || (currentMedia && currentMedia.media_url)) && (
        <div className="cdw-media-frame-overlay-top">
          {isCarousel && (
            <div className="cdw-media-view-toggle" role="group" aria-label="Carousel view mode">
              <button
                className={`cdw-media-view-toggle__btn${mediaViewMode === 'single' ? ' cdw-media-view-toggle__btn--active' : ''}`}
                onClick={() => setMediaViewMode('single')}
                title="Show one slide at a time"
                aria-label="Single slide view"
              >
                <Icon name="single" />
                <span>Single</span>
              </button>
              <button
                className={`cdw-media-view-toggle__btn${mediaViewMode === 'grid' ? ' cdw-media-view-toggle__btn--active' : ''}`}
                onClick={() => setMediaViewMode('grid')}
                title="Show all slides in a grid"
                aria-label="Grid view"
              >
                <Icon name="grid" />
                <span>Grid</span>
              </button>
            </div>
          )}

          {currentMedia && currentMedia.media_url && (
            <div className="cdw-media-frame__actions">
              <button
                className="cdw-overlay-icon-btn"
                onClick={() =>
                  onGenerateMedia({
                    mediaIndex: currentMediaIndex,
                    mediaId: currentMedia.id,
                  })
                }
                disabled={isCurrentSlideBusy}
                aria-label={
                  mediaCount > 1
                    ? `Re-render slide ${currentMediaIndex + 1} only`
                    : 'Re-render this image'
                }
                title={
                  mediaCount > 1
                    ? `Re-render the image for slide ${currentMediaIndex + 1} using AI. Other slides are not touched. Caption stays unchanged.`
                    : 'Ask AI to create a new version of this image. Caption stays unchanged.'
                }
              >
                <Icon name="refresh" />
                <span>
                  {isCurrentMediaRegenerating
                    ? 'Rendering…'
                    : mediaCount > 1
                      ? 'Re-render slide'
                      : 'Re-render'}
                </span>
              </button>
              {mediaCount > 1 && (
                <button
                  className="cdw-overlay-icon-btn"
                  onClick={() => onGenerateMedia({ regenerateAll: true })}
                  disabled={isMultiSlideBusy}
                  aria-label={`Re-render all ${mediaCount} slides`}
                  title={`Ask AI to re-render all ${mediaCount} slide images at once. Caption and hashtags stay unchanged.`}
                >
                  <Icon name="refreshAll" />
                  <span>
                    {multiSlideJobInFlight && regeneratingAllMedia
                      ? `Rendering all…`
                      : `Re-render all ${mediaCount}`}
                  </span>
                </button>
              )}
              {!isVideoFormat && (
                <button
                  className="cdw-overlay-icon-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCurrentSlideBusy}
                  aria-label="Replace this slide image with one from your device"
                  title="Replace this slide's image with a file from your device. This does not upload to Instagram — use 'Publish' for that."
                >
                  <Icon name="upload" />
                  <span>Replace image</span>
                </button>
              )}
              <button
                className={`cdw-overlay-icon-btn${currentMedia.is_hidden ? ' cdw-overlay-icon-btn--active' : ''}`}
                onClick={() => onToggleHideMedia(currentMedia.id, !currentMedia.is_hidden)}
                aria-label={
                  currentMedia.is_hidden
                    ? 'Include this slide in the post'
                    : 'Exclude this slide from the post'
                }
                title={
                  currentMedia.is_hidden
                    ? 'This slide is currently excluded from the post. Click to include it again.'
                    : "Exclude this slide from the post. It stays saved but won't be published."
                }
              >
                <Icon name={currentMedia.is_hidden ? 'show' : 'hide'} />
                <span>{currentMedia.is_hidden ? 'Include' : 'Exclude'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {mediaViewMode === 'grid' && isCarousel ? (
        <div className="cdw-carousel-grid" role="list" aria-label="Carousel slides grid">
          {contentItem!.media!.map((media, index) => (
            <button
              key={media.id}
              className={`cdw-carousel-grid__item${index === currentMediaIndex ? ' cdw-carousel-grid__item--active' : ''}${media.is_hidden ? ' cdw-carousel-grid__item--hidden' : ''}`}
              onClick={() => {
                onSetMediaIndex(index);
                openLightbox(index);
              }}
              role="listitem"
              title={`Select slide ${index + 1}${media.is_hidden ? ' (hidden from post)' : ''}`}
              aria-label={`Select slide ${index + 1}${media.is_hidden ? ' (hidden from post)' : ''}`}
            >
              {media.media_url ? (
                <img
                  src={media.media_url}
                  alt={`Slide ${index + 1}`}
                  className="cdw-carousel-grid__image"
                />
              ) : (
                <div className="cdw-carousel-grid__pending">
                  <span className="cdw-carousel-grid__pending-label">No image yet</span>
                  <button
                    className="cdw-carousel-grid__generate-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetMediaIndex(index);
                      onGenerateMedia({ mediaIndex: index, mediaId: media.id });
                    }}
                    disabled={generatingMediaIds.has(media.id) || multiSlideJobInFlight}
                    title={`Ask AI to create an image for slide ${index + 1} using its prompt`}
                    aria-label={`Create image for slide ${index + 1}`}
                  >
                    <Icon name="generate" />
                    {generatingMediaIds.has(media.id) ? 'Creating…' : 'Create image'}
                  </button>
                </div>
              )}
              <span className="cdw-carousel-grid__badge">{index + 1}</span>
            </button>
          ))}
        </div>
      ) : currentMedia?.media_url ? (
        currentMedia.media_type === 'video' ? (
          <video
            src={currentMedia.media_url}
            controls
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: currentMedia.is_hidden ? 0.4 : 1,
            }}
          />
        ) : (
          <button
            className="cdw-media-click-target"
            type="button"
            onClick={() => openLightbox(currentMediaIndex)}
            aria-label={`Open slide ${currentMediaIndex + 1} in full view`}
            title="Open full image"
          >
            <img
              src={currentMedia.media_url}
              alt={`Slide ${currentMediaIndex + 1}`}
              className="cdw-media-image"
              style={{ opacity: currentMedia.is_hidden ? 0.4 : 1 }}
            />
          </button>
        )
      ) : (
        <div
          className={`cdw-media-pending cdw-media-pending--drop-zone${isDragOver ? ' cdw-media-pending--dragging' : ''}`}
          style={{ opacity: currentMedia?.is_hidden ? 0.5 : 1 }}
        >
          <p className="cdw-media-pending__title">
            {isVideoFormat
              ? 'Video not generated yet'
              : `Slide ${currentMediaIndex + 1} — no image yet`}
          </p>
          {currentMediaPrompt ? (
            <div className="cdw-media-pending__prompt">
              <span className="cdw-media-pending__badge">AI prompt ready</span>
              <p className="cdw-media-pending__prompt-text">{currentMediaPrompt}</p>
            </div>
          ) : (
            <p className="cdw-media-pending__empty">No prompt set yet for this slide.</p>
          )}
          {!isVideoFormat && (
            <div className="cdw-media-pending__actions">
              <button
                className="cdw-media-pending__generate-btn"
                onClick={() =>
                  onGenerateMedia({ mediaIndex: currentMediaIndex, mediaId: currentMedia?.id })
                }
                disabled={isCurrentSlideBusy}
                title="Ask AI to create an image for this slide using this prompt"
              >
                <Icon name="generate" />
                {isCurrentMediaRegenerating ? 'Creating image…' : 'Create image with AI'}
              </button>
              <p className="cdw-media-pending__drop-hint">or drop an image file here instead</p>
            </div>
          )}
        </div>
      )}

      {/* Carousel controls */}
      {mediaCount > 1 && (
        <>
          <button
            onClick={onPrevMedia}
            aria-label="Previous slide"
            className="cdw-carousel-nav cdw-carousel-nav--left"
          >
            <Icon name="chevronLeft" />
          </button>
          <button
            onClick={onNextMedia}
            aria-label="Next slide"
            className="cdw-carousel-nav cdw-carousel-nav--right"
          >
            <Icon name="chevronRight" />
          </button>
          <div className="cdw-carousel-dots">
            {contentItem!.media!.map((m, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Go to slide ${index + 1}${m.is_hidden ? ' (hidden from post)' : ''}`}
                onClick={() => onSetMediaIndex(index)}
                onKeyDown={(e) => e.key === 'Enter' && onSetMediaIndex(index)}
                className={`cdw-carousel-dot${index === currentMediaIndex ? ' cdw-carousel-dot--active' : ''}${m.is_hidden ? ' cdw-carousel-dot--hidden' : ''}`}
              />
            ))}
          </div>
        </>
      )}

      {currentMedia?.media_type !== 'video' && currentMedia?.media_url && (
        <div className="cdw-media-frame__hint">Click image to open full view</div>
      )}

      {lightboxIndex !== null && contentItem?.media && contentItem.media.length > 0 && (
        <MediaLightboxModal
          mediaItems={contentItem.media}
          currentIndex={lightboxIndex}
          onChangeIndex={(nextIndex) => {
            setLightboxIndex(nextIndex);
            onSetMediaIndex(nextIndex);
          }}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

// ── CTA / sign-off image card ─────────────────────────────────────────────

function CtaSignoffCard({
  workspace,
  contentItem,
  outline,
  togglingCta,
  onToggleCtaImage,
  onGoToCtaSettings,
}: Pick<
  ContentDetailWorkspaceProps,
  'workspace' | 'contentItem' | 'outline' | 'togglingCta' | 'onToggleCtaImage' | 'onGoToCtaSettings'
>) {
  const formatForCta =
    contentItem?.content_format === 'image' ||
    contentItem?.content_format === 'carousel' ||
    outline.content_format === 'image' ||
    outline.content_format === 'carousel';
  if (!formatForCta) return null;

  const ctaUrl = workspace.cta_image_url || '';
  const workspaceDefault = Boolean(workspace.cta_image_include_by_default);
  const override = (contentItem?.include_cta_image ?? null) as boolean | null;
  const effectiveOn = Boolean(contentItem?.effective_cta_enabled);

  // Empty state — no CTA configured yet for the workspace
  if (!ctaUrl) {
    return (
      <div className="cdw-cta-card cdw-cta-card--empty">
        <div className="cdw-cta-card__icon">📣</div>
        <div className="cdw-cta-card__body">
          <p className="cdw-cta-card__title">Add a sign-off slide for every post</p>
          <p className="cdw-cta-card__desc">
            Create one closing image (e.g. “Follow for more”) and we&apos;ll auto-append it to all
            your image and carousel posts. You can toggle it per post.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onGoToCtaSettings}>
          Set up CTA image
        </Button>
      </div>
    );
  }

  const isCarousel =
    contentItem?.content_format === 'carousel' || outline.content_format === 'carousel';
  const slideHint = isCarousel ? 'Appended as the last slide' : 'Becomes slide 2 (carousel)';
  const sourceHint =
    override === null
      ? `Following your default (${workspaceDefault ? 'On' : 'Off'})`
      : 'Set just for this post';

  return (
    <div className={`cdw-cta-card${effectiveOn ? ' cdw-cta-card--on' : ' cdw-cta-card--off'}`}>
      <div className="cdw-cta-card__thumb">
        <img src={ctaUrl} alt="Sign-off slide" />
        <span
          className={`cdw-cta-card__chip${effectiveOn ? ' cdw-cta-card__chip--on' : ''}`}
          aria-label={effectiveOn ? 'CTA included on publish' : 'CTA excluded from this post'}
        >
          {effectiveOn ? '+ slide' : 'off'}
        </span>
      </div>
      <div className="cdw-cta-card__body">
        <p className="cdw-cta-card__title">Sign-off slide</p>
        <p className="cdw-cta-card__desc">
          {effectiveOn ? slideHint : 'Not included on this post.'}{' '}
          <span className="cdw-cta-card__source">{sourceHint}.</span>
        </p>
      </div>
      <div className="cdw-cta-card__actions">
        <button
          type="button"
          role="switch"
          aria-checked={effectiveOn}
          aria-label={effectiveOn ? 'Disable sign-off slide on this post' : 'Include sign-off slide on this post'}
          disabled={togglingCta}
          className={`cdw-cta-toggle${effectiveOn ? ' cdw-cta-toggle--on' : ''}`}
          onClick={() => onToggleCtaImage(effectiveOn ? false : true)}
        >
          <span className="cdw-cta-toggle__thumb" />
        </button>
        {override !== null && (
          <button
            type="button"
            className="cdw-cta-card__reset"
            onClick={() => onToggleCtaImage(null)}
            disabled={togglingCta}
            title={`Reset to your workspace default (${workspaceDefault ? 'On' : 'Off'})`}
          >
            Reset to default
          </button>
        )}
      </div>
    </div>
  );
}


// ── Post export actions (copy caption / download all media) ─────────────────

// Lets the operator post by hand when the Instagram connection is down:
// one-click copy of the caption (+ hashtags) and a ZIP of all post images.
function PostExportActions({ contentItem }: { contentItem: ContentItem }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const hasGeneratedMedia = Boolean(contentItem.media?.some((m) => m.media_url));
  // Mirror the publish format: caption and hashtags joined by a blank line.
  const captionText = [contentItem.caption?.trim(), contentItem.hashtags?.trim()]
    .filter(Boolean)
    .join('\n\n');

  const handleCopy = async () => {
    if (!captionText) return;
    try {
      await navigator.clipboard.writeText(captionText);
    } catch {
      // Fallback for browsers/contexts where the async Clipboard API is blocked.
      const textarea = document.createElement('textarea');
      textarea.value = captionText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch {
        /* clipboard unavailable — nothing more we can do */
      }
      textarea.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const { blob, filename } = await downloadContentItemMedia(contentItem.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!captionText && !hasGeneratedMedia) {
    return null;
  }

  return (
    <div className="cdw-export-actions">
      {captionText && (
        <button
          type="button"
          className="cdw-export-btn"
          onClick={handleCopy}
          title="Copy the caption and hashtags to paste into Instagram"
        >
          {copied ? '✓ Caption copied' : '📋 Copy caption'}
        </button>
      )}
      {hasGeneratedMedia && (
        <button
          type="button"
          className="cdw-export-btn"
          onClick={handleDownload}
          disabled={downloading}
          title="Download all images for this post as a ZIP for manual upload"
        >
          {downloading ? 'Preparing…' : '⬇ Download all'}
        </button>
      )}
      {downloadError && <span className="cdw-export-actions__error">{downloadError}</span>}
    </div>
  );
}

// ── Preview tab ────────────────────────────────────────────────────────────

function PreviewTab(
  props: Pick<
    ContentDetailWorkspaceProps,
    | 'outline'
    | 'contentItem'
    | 'currentMediaIndex'
    | 'onPrevMedia'
    | 'onNextMedia'
    | 'onSetMediaIndex'
    | 'generatingContent'
    | 'generatingMediaIds'
    | 'multiSlideJobInFlight'
    | 'regeneratingAllMedia'
    | 'onGenerateContent'
    | 'onRegenerateContent'
    | 'onGenerateMedia'
    | 'onApprove'
    | 'onApproveContentItem'
    | 'onOpenPublishModal'
    | 'onToggleHideMedia'
    | 'onUploadMediaFile'
    | 'workspace'
    | 'onToggleCtaImage'
    | 'togglingCta'
    | 'onGoToCtaSettings'
  >
) {
  const {
    outline,
    contentItem,
    generatingContent,
    generatingMediaIds,
    multiSlideJobInFlight,
    onGenerateContent,
    onRegenerateContent,
    onGenerateMedia,
    onApprove,
    onApproveContentItem,
    onOpenPublishModal,
  } = props;
  // Any media job in flight blocks the "Generate Media" / "Skip approval" CTAs in this tab.
  const anyMediaJobInFlight = multiSlideJobInFlight || generatingMediaIds.size > 0;

  const hasMedia = Boolean(contentItem?.media && contentItem.media.length > 0);
  const hasGeneratedMedia = hasMedia && contentItem!.media!.some((m) => m.media_url);
  const isVideoFormat =
    contentItem?.content_format === 'video' || outline.content_format === 'video';
  const needsApproval = outline.status === 'draft' || outline.status === 'rejected';
  const contentNeedsApproval =
    contentItem?.status === 'draft' || contentItem?.status === 'rejected';

  // Derive primary CTA
  let primaryCta: React.ReactNode = null;
  if (!contentItem) {
    primaryCta = (
      <button
        className="cdw-preview-cta__primary cdw-preview-cta__primary--generate"
        disabled={generatingContent}
        onClick={onGenerateContent}
      >
        {generatingContent
          ? 'Generating…'
          : needsApproval
            ? '✨ Generate Item'
            : '✨ Generate Content'}
      </button>
    );
  } else if (contentNeedsApproval) {
    // Content item generated but awaiting user approval
    primaryCta = (
      <button
        className="cdw-preview-cta__primary cdw-preview-cta__primary--approve"
        onClick={onApproveContentItem}
      >
        ✓ Approve Content
      </button>
    );
  } else if (!hasGeneratedMedia) {
    primaryCta = (
      <button
        className="cdw-preview-cta__primary cdw-preview-cta__primary--generate"
        disabled={anyMediaJobInFlight}
        onClick={() => onGenerateMedia()}
      >
        {anyMediaJobInFlight
          ? 'Generating…'
          : isVideoFormat
            ? '🎬 Generate Video'
            : '🎨 Generate Media'}
      </button>
    );
  } else if (contentItem.status !== 'published' && contentItem.status !== 'scheduled') {
    primaryCta = (
      <button
        className="cdw-preview-cta__primary cdw-preview-cta__primary--schedule"
        onClick={onOpenPublishModal}
      >
        📤 Upload Now
      </button>
    );
  } else if (contentItem.status === 'scheduled') {
    primaryCta = <span className="cdw-preview-cta__status">📅 Scheduled for publishing</span>;
  } else {
    primaryCta = (
      <span className="cdw-preview-cta__status">
        {contentItem.published_via === 'manual' ? '✅ Manually uploaded' : '✅ Published'}
      </span>
    );
  }

  // Secondary action
  const secondaryCta = contentItem ? (
    contentNeedsApproval ? (
      // When awaiting content approval, offer quick-skip to media generation
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onGenerateMedia()}
        disabled={anyMediaJobInFlight}
      >
        {anyMediaJobInFlight ? 'Generating…' : '🎨 Skip approval & generate media'}
      </Button>
    ) : (
      <Button variant="ghost" size="sm" onClick={onRegenerateContent} disabled={generatingContent}>
        {generatingContent ? 'Refreshing text…' : '🔄 Rewrite caption + hashtags + prompts'}
      </Button>
    )
  ) : needsApproval ? (
    <button className="cdw-preview-cta__subtle" onClick={onApprove}>
      ✓ Approve outline only
    </button>
  ) : null;

  return (
    <div className="cdw-preview">
      {/* Outline brief — always visible when no content exists */}
      {!contentItem && (outline.title || outline.summary) && (
        <div className="cdw-outline-brief">
          <h2 className="cdw-outline-brief__title">{outline.title}</h2>
          {outline.summary && <p className="cdw-outline-brief__summary">{outline.summary}</p>}
        </div>
      )}

      <div className="cdw-media-card">
        <MediaFrame {...props} />

        {contentItem && (
          <div className="cdw-media-caption">
            {contentItem.caption ? (
              <p className="cdw-media-caption__text">{contentItem.caption}</p>
            ) : (
              <p className="cdw-media-caption__placeholder">No caption yet</p>
            )}
            {contentItem.hashtags && (
              <p className="cdw-media-caption__hashtags">{contentItem.hashtags}</p>
            )}
            <PostExportActions contentItem={contentItem} />
          </div>
        )}
      </div>

      {contentItem && (
        <CtaSignoffCard
          workspace={props.workspace}
          contentItem={contentItem}
          outline={outline}
          togglingCta={props.togglingCta}
          onToggleCtaImage={props.onToggleCtaImage}
          onGoToCtaSettings={props.onGoToCtaSettings}
        />
      )}

      <div className="cdw-preview-cta">
        {primaryCta}
        {!contentItem && needsApproval && (
          <p className="cdw-preview-cta__hint">Generate Item will auto-approve this outline.</p>
        )}
        {contentNeedsApproval && (
          <p className="cdw-preview-cta__hint">
            AI generated this content. Review the caption and prompts, then approve to continue.
          </p>
        )}
        {secondaryCta}
      </div>
    </div>
  );
}

// ── Edit tab ───────────────────────────────────────────────────────────────

function EditTab(
  props: Pick<
    ContentDetailWorkspaceProps,
    | 'outline'
    | 'contentItem'
    | 'allowedFormats'
    | 'generatingContent'
    | 'generatingMediaIds'
    | 'multiSlideJobInFlight'
    | 'regeneratingAllMedia'
    | 'onGenerateContent'
    | 'onRegenerateContent'
    | 'onGenerateMedia'
    | 'editingCaption'
    | 'captionDraft'
    | 'savingCaption'
    | 'onStartEditCaption'
    | 'onCaptionDraftChange'
    | 'onSaveCaption'
    | 'onCancelEditCaption'
    | 'editingHashtags'
    | 'hashtagsDraft'
    | 'savingHashtags'
    | 'onStartEditHashtags'
    | 'onHashtagsDraftChange'
    | 'onSaveHashtags'
    | 'onCancelEditHashtags'
    | 'editingPromptId'
    | 'promptDraft'
    | 'savingPrompt'
    | 'onStartEditPrompt'
    | 'onPromptDraftChange'
    | 'onSavePrompt'
    | 'onCancelEditPrompt'
    | 'onDeleteMedia'
    | 'onAddMediaPrompt'
    | 'onReorderMedia'
    | 'onUploadMediaFile'
    | 'onToggleHideMedia'
    | 'savingOutlineMeta'
    | 'onSaveOutlineMeta'
  >
) {
  const {
    outline,
    contentItem,
    allowedFormats,
    generatingContent,
    generatingMediaIds,
    multiSlideJobInFlight,
    regeneratingAllMedia,
    onGenerateContent,
    onRegenerateContent,
    onGenerateMedia,
    editingCaption,
    captionDraft,
    savingCaption,
    onStartEditCaption,
    onCaptionDraftChange,
    onSaveCaption,
    onCancelEditCaption,
    editingHashtags,
    hashtagsDraft,
    savingHashtags,
    onStartEditHashtags,
    onHashtagsDraftChange,
    onSaveHashtags,
    onCancelEditHashtags,
    editingPromptId,
    promptDraft,
    savingPrompt,
    onStartEditPrompt,
    onPromptDraftChange,
    onSavePrompt,
    onCancelEditPrompt,
    onDeleteMedia,
    onAddMediaPrompt,
    onReorderMedia,
    onUploadMediaFile,
    onToggleHideMedia,
    savingOutlineMeta,
    onSaveOutlineMeta,
  } = props;

  // New image prompt addition state (local to EditTab)
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [newPromptText, setNewPromptText] = useState('');

  // Delete media confirmation state (local to EditTab)
  const [deleteMediaId, setDeleteMediaId] = useState<number | null>(null);

  // Outline metadata edit state (local to EditTab)
  const [editingOutlineMeta, setEditingOutlineMeta] = useState(false);
  const [outlineMetaError, setOutlineMetaError] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState(outline.title || '');
  const [summaryDraft, setSummaryDraft] = useState(outline.summary || '');
  const [formatDraft, setFormatDraft] = useState(outline.content_format || 'image');
  const [publishDateDraft, setPublishDateDraft] = useState('');

  // Drag-and-drop reorder state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const ONE_MINUTE_MS = 60_000;
  const isPublishedPost =
    outline.status === 'published' ||
    contentItem?.status === 'published' ||
    Boolean(contentItem?.published_at);

  const toLocalDateTimeValue = (isoDate: string): string => {
    const d = new Date(isoDate);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatLabel = (value: string): string => {
    if (!value) return 'Unknown';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const startEditOutlineMeta = () => {
    setTitleDraft(outline.title || '');
    setSummaryDraft(outline.summary || '');
    setFormatDraft(outline.content_format || allowedFormats[0] || 'image');
    setPublishDateDraft(outline.publish_at ? toLocalDateTimeValue(outline.publish_at) : '');
    setOutlineMetaError(null);
    setEditingOutlineMeta(true);
  };

  const cancelEditOutlineMeta = () => {
    setOutlineMetaError(null);
    setEditingOutlineMeta(false);
  };

  const saveOutlineMeta = async () => {
    const cleanedTitle = titleDraft.trim();
    if (!cleanedTitle) {
      setOutlineMetaError('Title is required.');
      return;
    }

    let publishAtIso: string | null | undefined;
    if (!isPublishedPost && publishDateDraft) {
      const selectedDate = new Date(publishDateDraft);
      if (selectedDate.getTime() <= Date.now()) {
        setOutlineMetaError(
          "You can't set a publish date in the past. Please choose a future date and time."
        );
        return;
      }
      publishAtIso = selectedDate.toISOString();
    } else if (!isPublishedPost) {
      publishAtIso = null;
    }

    setOutlineMetaError(null);
    await onSaveOutlineMeta({
      title: cleanedTitle,
      summary: summaryDraft,
      content_format: formatDraft,
      ...(isPublishedPost ? {} : { publish_at: publishAtIso }),
    });
    setEditingOutlineMeta(false);
  };

  const isVideoFormat =
    contentItem?.content_format === 'video' || outline.content_format === 'video';
  const mediaItems = contentItem?.media ?? [];
  const hasMedia = mediaItems.length > 0;

  const handleAddPromptConfirm = () => {
    if (!newPromptText.trim()) return;
    onAddMediaPrompt(newPromptText.trim());
    setNewPromptText('');
    setAddingPrompt(false);
  };

  // Drag-and-drop reorder handlers
  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const ids = mediaItems.map((m) => m.id);
    const reordered = [...ids];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onReorderMedia(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  if (!contentItem) {
    return (
      <div className="cdw-edit">
        <div className="cdw-edit-card">
          <div className="cdw-edit-card__header">
            <span className="cdw-edit-card__title">Post Setup</span>
            {!editingOutlineMeta && (
              <Button variant="ghost" size="sm" onClick={startEditOutlineMeta}>
                ✏️ Edit
              </Button>
            )}
          </div>
          <div className="cdw-edit-card__body">
            <p className="cdw-edit-meta-note">
              Title and description are used to create AI content. If content already exists,
              changing these fields won&apos;t rewrite it automatically.
            </p>
            {editingOutlineMeta ? (
              <>
                <div className="cdw-edit-meta-grid">
                  <label className="cdw-edit-meta-field">
                    <span>Title</span>
                    <input
                      className="cdw-edit-input"
                      value={titleDraft}
                      onChange={(e) => {
                        setTitleDraft(e.target.value);
                        setOutlineMetaError(null);
                      }}
                      maxLength={255}
                      disabled={savingOutlineMeta}
                    />
                  </label>
                  <label className="cdw-edit-meta-field">
                    <span>Format</span>
                    <select
                      className="cdw-edit-input"
                      value={formatDraft}
                      onChange={(e) => setFormatDraft(e.target.value)}
                      disabled={savingOutlineMeta}
                    >
                      {allowedFormats.map((format) => (
                        <option key={format} value={format}>
                          {formatLabel(format)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="cdw-edit-meta-field cdw-edit-meta-field--full">
                    <span>Description</span>
                    <textarea
                      className="cdw-edit-textarea"
                      rows={4}
                      value={summaryDraft}
                      onChange={(e) => {
                        setSummaryDraft(e.target.value);
                        setOutlineMetaError(null);
                      }}
                      disabled={savingOutlineMeta}
                    />
                  </label>
                  <label className="cdw-edit-meta-field">
                    <span>Publish date</span>
                    <input
                      type="datetime-local"
                      className="cdw-edit-input"
                      value={publishDateDraft}
                      min={toLocalDateTimeValue(new Date(Date.now() + ONE_MINUTE_MS).toISOString())}
                      onChange={(e) => {
                        setPublishDateDraft(e.target.value);
                        setOutlineMetaError(null);
                      }}
                      disabled={savingOutlineMeta || isPublishedPost}
                    />
                    {isPublishedPost && (
                      <small className="cdw-edit-placeholder">
                        Publish date is locked after a post is published.
                      </small>
                    )}
                  </label>
                </div>
                {outlineMetaError && <p className="cdw-publish-date-row__error">🚫 {outlineMetaError}</p>}
                <div className="cdw-edit-row-actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      void saveOutlineMeta();
                    }}
                    disabled={savingOutlineMeta}
                  >
                    {savingOutlineMeta ? 'Saving…' : 'Save setup'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={cancelEditOutlineMeta}
                    disabled={savingOutlineMeta}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="cdw-edit-meta-grid">
                <div className="cdw-edit-meta-field">
                  <span>Title</span>
                  <p className="cdw-edit-view">{outline.title || 'Untitled post'}</p>
                </div>
                <div className="cdw-edit-meta-field">
                  <span>Format</span>
                  <p className="cdw-edit-view">{formatLabel(outline.content_format)}</p>
                </div>
                <div className="cdw-edit-meta-field cdw-edit-meta-field--full">
                  <span>Description</span>
                  {outline.summary ? (
                    <p className="cdw-edit-view">{outline.summary}</p>
                  ) : (
                    <p className="cdw-edit-placeholder">No description yet.</p>
                  )}
                </div>
                <div className="cdw-edit-meta-field">
                  <span>Publish date</span>
                  <p className="cdw-edit-view">
                    {outline.publish_at ? `📅 ${new Date(outline.publish_at).toLocaleString()}` : '—'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="cdw-edit-card">
          <div className="cdw-edit-card__body">
            <p style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-sm)', margin: 0 }}>
              No content yet — generate content first from the Preview tab.
            </p>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Button variant="primary" onClick={onGenerateContent} disabled={generatingContent}>
                {generatingContent ? 'Generating…' : '✨ Generate Content'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Delete confirmation modal (inline)
  if (deleteMediaId !== null) {
    const targetMedia = mediaItems.find((m) => m.id === deleteMediaId);
    const idx = mediaItems.findIndex((m) => m.id === deleteMediaId);
    return (
      <div className="cdw-edit">
        <div className="cdw-edit-card">
          <div className="cdw-edit-card__header">
            <span className="cdw-edit-card__title">🗑️ Delete Slide {idx + 1}</span>
          </div>
          <div className="cdw-edit-card__body">
            <p style={{ marginBottom: 8 }}>
              Are you sure you want to delete this slide? This will permanently remove the image
              {targetMedia?.prompt ? ' and its prompt' : ''}.
            </p>
            {targetMedia?.media_url && (
              <img
                src={targetMedia.media_url}
                alt="Slide to delete"
                style={{
                  width: 80,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 6,
                  marginBottom: 8,
                }}
              />
            )}
            <div className="cdw-edit-row-actions">
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  onDeleteMedia(deleteMediaId);
                  setDeleteMediaId(null);
                }}
              >
                🗑️ Delete Slide
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setDeleteMediaId(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cdw-edit">
      <div className="cdw-edit-card">
        <div className="cdw-edit-card__header">
          <span className="cdw-edit-card__title">Post Setup</span>
          {!editingOutlineMeta && (
            <Button variant="ghost" size="sm" onClick={startEditOutlineMeta}>
              ✏️ Edit
            </Button>
          )}
        </div>
        <div className="cdw-edit-card__body">
          <p className="cdw-edit-meta-note">
            Title and description are used to create AI content. Since content already exists for
            this post, these fields won&apos;t rewrite caption/media unless you regenerate.
          </p>
          {editingOutlineMeta ? (
            <>
              <div className="cdw-edit-meta-grid">
                <label className="cdw-edit-meta-field">
                  <span>Title</span>
                  <input
                    className="cdw-edit-input"
                    value={titleDraft}
                    onChange={(e) => {
                      setTitleDraft(e.target.value);
                      setOutlineMetaError(null);
                    }}
                    maxLength={255}
                    disabled={savingOutlineMeta}
                  />
                </label>
                <label className="cdw-edit-meta-field">
                  <span>Format</span>
                  <select
                    className="cdw-edit-input"
                    value={formatDraft}
                    onChange={(e) => setFormatDraft(e.target.value)}
                    disabled={savingOutlineMeta}
                  >
                    {allowedFormats.map((format) => (
                      <option key={format} value={format}>
                        {formatLabel(format)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cdw-edit-meta-field cdw-edit-meta-field--full">
                  <span>Description</span>
                  <textarea
                    className="cdw-edit-textarea"
                    rows={4}
                    value={summaryDraft}
                    onChange={(e) => {
                      setSummaryDraft(e.target.value);
                      setOutlineMetaError(null);
                    }}
                    disabled={savingOutlineMeta}
                  />
                </label>
                <label className="cdw-edit-meta-field">
                  <span>Publish date</span>
                  <input
                    type="datetime-local"
                    className="cdw-edit-input"
                    value={publishDateDraft}
                    min={toLocalDateTimeValue(new Date(Date.now() + ONE_MINUTE_MS).toISOString())}
                    onChange={(e) => {
                      setPublishDateDraft(e.target.value);
                      setOutlineMetaError(null);
                    }}
                    disabled={savingOutlineMeta || isPublishedPost}
                  />
                  {isPublishedPost && (
                    <small className="cdw-edit-placeholder">
                      Publish date is locked after a post is published.
                    </small>
                  )}
                </label>
              </div>
              {outlineMetaError && <p className="cdw-publish-date-row__error">🚫 {outlineMetaError}</p>}
              <div className="cdw-edit-row-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    void saveOutlineMeta();
                  }}
                  disabled={savingOutlineMeta}
                >
                  {savingOutlineMeta ? 'Saving…' : 'Save setup'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={cancelEditOutlineMeta}
                  disabled={savingOutlineMeta}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div className="cdw-edit-meta-grid">
              <div className="cdw-edit-meta-field">
                <span>Title</span>
                <p className="cdw-edit-view">{outline.title || 'Untitled post'}</p>
              </div>
              <div className="cdw-edit-meta-field">
                <span>Format</span>
                <p className="cdw-edit-view">{formatLabel(outline.content_format)}</p>
              </div>
              <div className="cdw-edit-meta-field cdw-edit-meta-field--full">
                <span>Description</span>
                {outline.summary ? (
                  <p className="cdw-edit-view">{outline.summary}</p>
                ) : (
                  <p className="cdw-edit-placeholder">No description yet.</p>
                )}
              </div>
              <div className="cdw-edit-meta-field">
                <span>Publish date</span>
                <p className="cdw-edit-view">
                  {outline.publish_at ? `📅 ${new Date(outline.publish_at).toLocaleString()}` : '—'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Caption */}
      <div className="cdw-edit-card">
        <div className="cdw-edit-card__header">
          <span className="cdw-edit-card__title">Caption</span>
          {!editingCaption && (
            <Button variant="ghost" size="sm" onClick={onStartEditCaption}>
              ✏️ Edit
            </Button>
          )}
        </div>
        <div className="cdw-edit-card__body">
          {editingCaption ? (
            <>
              <textarea
                className="cdw-edit-textarea"
                value={captionDraft}
                onChange={(e) => onCaptionDraftChange(e.target.value)}
                rows={6}
                disabled={savingCaption}
              />
              <div className="cdw-edit-row-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onSaveCaption}
                  disabled={savingCaption}
                >
                  {savingCaption ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onCancelEditCaption}
                  disabled={savingCaption}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : contentItem.caption ? (
            <p className="cdw-edit-view">{contentItem.caption}</p>
          ) : (
            <p className="cdw-edit-placeholder">No caption — click Edit to add one.</p>
          )}
        </div>
      </div>

      {/* Hashtags */}
      <div className="cdw-edit-card">
        <div className="cdw-edit-card__header">
          <span className="cdw-edit-card__title">Hashtags</span>
          {!editingHashtags && (
            <Button variant="ghost" size="sm" onClick={onStartEditHashtags}>
              ✏️ Edit
            </Button>
          )}
        </div>
        <div className="cdw-edit-card__body">
          {editingHashtags ? (
            <>
              <textarea
                className="cdw-edit-textarea"
                value={hashtagsDraft}
                onChange={(e) => onHashtagsDraftChange(e.target.value)}
                rows={3}
                placeholder="#motivation #fitness #gym"
                disabled={savingHashtags}
              />
              <div className="cdw-edit-row-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onSaveHashtags}
                  disabled={savingHashtags}
                >
                  {savingHashtags ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onCancelEditHashtags}
                  disabled={savingHashtags}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : contentItem.hashtags ? (
            <p className="cdw-edit-view" style={{ color: 'var(--color-primary-600)' }}>
              {contentItem.hashtags}
            </p>
          ) : (
            <p className="cdw-edit-placeholder">No hashtags — click Edit to add some.</p>
          )}
        </div>
      </div>

      {/* Media prompts */}
      {hasMedia && (
        <div className="cdw-edit-card">
          <div className="cdw-edit-card__header">
            <span className="cdw-edit-card__title">
              {isVideoFormat
                ? 'Video Prompt'
                : `Image Prompts (${mediaItems.length} slide${mediaItems.length > 1 ? 's' : ''})`}
            </span>
            {!isVideoFormat && !addingPrompt && (
              <Button variant="ghost" size="sm" onClick={() => setAddingPrompt(true)}>
                ＋ Add Slide
              </Button>
            )}
          </div>
          <div className="cdw-edit-card__body">
            <div className="cdw-prompt-list">
              {mediaItems.map((media, idx) => (
                <div
                  key={media.id}
                  className={`cdw-prompt-item${dragOverIndex === idx ? ' cdw-prompt-item--drag-over' : ''}${media.is_hidden ? ' cdw-prompt-item--hidden' : ''}`}
                  draggable={!isVideoFormat}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="cdw-prompt-item__header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {!isVideoFormat && (
                        <span
                          className="cdw-drag-handle"
                          title="Drag to reorder"
                          aria-hidden="true"
                        >
                          ⠿
                        </span>
                      )}
                      <span className="cdw-prompt-item__label">
                        {isVideoFormat ? 'Video' : `Slide ${idx + 1}`}
                        {media.media_url && ' ✓'}
                        {media.is_hidden && <span className="cdw-hidden-badge"> 🚫 Hidden</span>}
                        {media.is_user_uploaded && (
                          <span
                            className="cdw-user-uploaded-badge"
                            title="This image was uploaded by you. AI generation will not overwrite it."
                            aria-label="User uploaded image – protected from AI overwrite"
                          >
                            {' '}
                            📁 Your upload
                          </span>
                        )}
                        {(media.source_kind === 'sourced' ||
                          media.source_kind === 'sourced_edited') && (
                          <span
                            className="cdw-real-photo-badge"
                            title={[
                              media.source_kind === 'sourced_edited'
                                ? 'A real licensed photograph, recomposed by the image model.'
                                : 'A real licensed photograph, published as licensed.',
                              media.source_credit
                                ? `Credit: ${media.source_credit} — added to your caption automatically.`
                                : 'No attribution required by this licence.',
                            ].join(' ')}
                          >
                            {' '}
                            📷 {media.source_kind === 'sourced_edited' ? 'Real photo, edited' : 'Real photo'}
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {editingPromptId !== media.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onStartEditPrompt(media.id, media.prompt || '')}
                        >
                          ✏️ Edit
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          onGenerateMedia({
                            mediaIndex: idx,
                            mediaId: media.id,
                          })
                        }
                        disabled={generatingMediaIds.has(media.id) || multiSlideJobInFlight}
                        title={`Regenerate only slide ${idx + 1} — other slides are not affected`}
                      >
                        {generatingMediaIds.has(media.id)
                          ? `Re-rendering…`
                          : `🔄 Re-render slide`}
                      </Button>
                      <button
                        className={`cdw-hide-btn${media.is_hidden ? ' cdw-hide-btn--hidden' : ''}`}
                        onClick={() => onToggleHideMedia(media.id, !media.is_hidden)}
                        title={media.is_hidden ? 'Show in post' : 'Hide from post'}
                      >
                        {media.is_hidden ? '👁️' : '🚫'}
                      </button>
                      <button
                        className="cdw-delete-media-btn"
                        onClick={() => setDeleteMediaId(media.id)}
                        title="Delete this slide"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {/* Per-slide image upload */}
                  {!isVideoFormat && (
                    <div className="cdw-prompt-item__upload">
                      <label
                        className="cdw-upload-label"
                        title="Upload a custom image for this slide"
                      >
                        📁 Upload image
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onUploadMediaFile(file, media.id);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  )}

                  <div className="cdw-prompt-item__body">
                    {editingPromptId === media.id ? (
                      <>
                        <textarea
                          className="cdw-edit-textarea"
                          value={promptDraft}
                          onChange={(e) => onPromptDraftChange(e.target.value)}
                          rows={3}
                          disabled={savingPrompt}
                        />
                        <div className="cdw-edit-row-actions">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => onSavePrompt(media.id)}
                            disabled={savingPrompt}
                          >
                            {savingPrompt ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={onCancelEditPrompt}
                            disabled={savingPrompt}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p
                        style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--color-neutral-600)',
                          fontStyle: 'italic',
                          lineHeight: 'var(--leading-snug)',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {media.prompt || 'No prompt set'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add new image prompt */}
            {addingPrompt && (
              <div className="cdw-add-prompt-form">
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    marginBottom: 8,
                    color: 'var(--color-neutral-700)',
                  }}
                >
                  ＋ Add new image prompt
                </p>
                <textarea
                  className="cdw-edit-textarea"
                  value={newPromptText}
                  onChange={(e) => setNewPromptText(e.target.value)}
                  rows={3}
                  placeholder="Describe the image for this slide…"
                  autoFocus
                />
                <div className="cdw-edit-row-actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddPromptConfirm}
                    disabled={!newPromptText.trim()}
                  >
                    Add Slide
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setAddingPrompt(false);
                      setNewPromptText('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add first image prompt when no media exists yet */}
      {!hasMedia && !isVideoFormat && (
        <div className="cdw-edit-card">
          <div className="cdw-edit-card__header">
            <span className="cdw-edit-card__title">Image Prompts</span>
          </div>
          <div className="cdw-edit-card__body">
            {addingPrompt ? (
              <div className="cdw-add-prompt-form">
                <textarea
                  className="cdw-edit-textarea"
                  value={newPromptText}
                  onChange={(e) => setNewPromptText(e.target.value)}
                  rows={3}
                  placeholder="Describe the image…"
                  autoFocus
                />
                <div className="cdw-edit-row-actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddPromptConfirm}
                    disabled={!newPromptText.trim()}
                  >
                    Add Slide
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setAddingPrompt(false);
                      setNewPromptText('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <p
                  style={{
                    color: 'var(--color-neutral-400)',
                    fontSize: 'var(--text-sm)',
                    margin: 0,
                  }}
                >
                  No image prompts yet.
                </p>
                <Button variant="ghost" size="sm" onClick={() => setAddingPrompt(true)}>
                  ＋ Add Prompt
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Regenerate actions */}
      <div className="cdw-regen-section">
        <p className="cdw-regen-section__title">Regenerate specific parts</p>
        <div className="cdw-regen-section__actions">
          <Button
            variant="primary"
            size="sm"
            onClick={onRegenerateContent}
            disabled={generatingContent}
            title="Rewrites caption, hashtags, and media prompts. Existing generated media files are not changed."
          >
            {generatingContent
              ? 'Rewriting text + prompts…'
              : '🔄 Rewrite caption, hashtags + prompts'}
          </Button>
          {hasMedia && !isVideoFormat && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onGenerateMedia({ regenerateAll: true })}
              disabled={multiSlideJobInFlight || generatingMediaIds.size > 0}
              title={`Re-renders all ${mediaItems.length} slide image${mediaItems.length > 1 ? 's' : ''}. Caption and hashtags are not changed.`}
            >
              {multiSlideJobInFlight && regeneratingAllMedia
                ? `Re-rendering all ${mediaItems.length} slides…`
                : `🎨 Re-render all ${mediaItems.length} slide image${mediaItems.length > 1 ? 's' : ''}`}
            </Button>
          )}
          {hasMedia && isVideoFormat && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onGenerateMedia({ regenerateAll: true })}
              disabled={multiSlideJobInFlight || generatingMediaIds.size > 0}
              title="Re-renders the video only. Caption and hashtags are not changed."
            >
              {multiSlideJobInFlight && regeneratingAllMedia
                ? 'Re-rendering video…'
                : '🎬 Re-render video only'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Meta & Workflow tab ────────────────────────────────────────────────────

function MetaTab(
  props: Pick<
    ContentDetailWorkspaceProps,
    | 'outline'
    | 'contentItem'
    | 'confirmDelete'
    | 'onConfirmDelete'
    | 'onCancelDelete'
    | 'onDelete'
    | 'formatDate'
    | 'getPillarName'
    | 'getPlanName'
    | 'schedulerTasks'
    | 'refreshingInstagramMetrics'
    | 'onRefreshInstagramMetrics'
  >
) {
  const {
    outline,
    contentItem,
    confirmDelete,
    onConfirmDelete,
    onCancelDelete,
    onDelete,
    formatDate,
    getPillarName,
    getPlanName,
    schedulerTasks,
    refreshingInstagramMetrics,
    onRefreshInstagramMetrics,
  } = props;

  const hasInstagramPostId = Boolean(contentItem?.instagram_post_id);
  const isPublished = contentItem?.status === 'published';

  // Build timeline steps
  type TimelineStep = {
    label: string;
    date?: string;
    done: boolean;
    active: boolean;
  };

  const steps: Omit<TimelineStep, 'active'>[] = [
    {
      label: 'Created',
      date: outline.created_at ? formatDate(outline.created_at) : undefined,
      done: true,
    },
    {
      label: 'Approved',
      date: undefined,
      done: ['approved', 'final', 'scheduled', 'published'].includes(outline.status),
    },
    {
      label: 'Content generated',
      date: undefined,
      done: Boolean(contentItem),
    },
    {
      label: 'Media generated',
      date: undefined,
      done: Boolean(contentItem?.media?.some((m) => m.media_url)),
    },
    {
      label: 'Scheduled',
      date: outline.publish_at ? formatDate(outline.publish_at) : undefined,
      done: contentItem?.status === 'scheduled' || contentItem?.status === 'published',
    },
    {
      label: 'Published',
      date: undefined,
      done: contentItem?.status === 'published',
    },
  ];

  // Mark active: the first incomplete step is the current one
  const currentStepIdx = steps.findIndex((s) => !s.done);
  const stepsWithActive: TimelineStep[] = steps.map((s, i) => ({
    ...s,
    active: i === currentStepIdx,
  }));

  const taskTypeLabel: Record<string, string> = {
    CREATE_ITEM: '📝 Create Content',
    CREATE_MEDIA: '🎨 Generate Media',
    PUBLISH: '🚀 Publish',
  };

  const taskStatusVariant = (status: string) => {
    switch (status) {
      case 'done':
        return 'published';
      case 'pending':
        return 'scheduled';
      case 'running':
        return 'draft';
      case 'failed':
        return 'rejected';
      case 'canceled':
        return 'neutral';
      case 'blocked':
        return 'draft';
      default:
        return 'neutral';
    }
  };

  return (
    <div className="cdw-meta">
      {/* Metadata grid */}
      <div className="cdw-meta-card">
        <div className="cdw-meta-card__header">Outline Details</div>
        <div className="cdw-meta-card__body">
          {outline.summary && (
            <p className="cdw-meta-summary" style={{ marginBottom: 'var(--space-4)' }}>
              {outline.summary}
            </p>
          )}
          <div className="cdw-meta-grid">
            <div>
              <p className="cdw-meta-item__label">Format</p>
              <p className="cdw-meta-item__value" style={{ textTransform: 'capitalize' }}>
                {outline.content_format}
              </p>
            </div>
            <div>
              <p className="cdw-meta-item__label">Status</p>
              <p className="cdw-meta-item__value">
                <Badge variant={statusVariant(outline.status)} dot>
                  {outline.status}
                </Badge>
              </p>
            </div>
            {outline.pillar && (
              <div>
                <p className="cdw-meta-item__label">Pillar</p>
                <p className="cdw-meta-item__value">🏛️ {getPillarName(outline.pillar)}</p>
              </div>
            )}
            {outline.plan && (
              <div>
                <p className="cdw-meta-item__label">Plan</p>
                <p className="cdw-meta-item__value">📋 {getPlanName(outline.plan)}</p>
              </div>
            )}
            <div>
              <p className="cdw-meta-item__label">Created</p>
              <p className="cdw-meta-item__value">{formatDate(outline.created_at)}</p>
            </div>
            {contentItem && (
              <div>
                <p className="cdw-meta-item__label">Content status</p>
                <p className="cdw-meta-item__value">
                  <Badge variant={statusVariant(contentItem.status)} dot>
                    {contentItem.status}
                  </Badge>
                </p>
              </div>
            )}
          </div>

          {contentItem && (
            <div className="cdw-instagram-metrics">
              <div className="cdw-instagram-metrics__header">
                <p className="cdw-meta-item__label">Instagram Metrics</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onRefreshInstagramMetrics}
                  disabled={
                    refreshingInstagramMetrics || !hasInstagramPostId || !isPublished
                  }
                  title={
                    hasInstagramPostId
                      ? 'Fetch latest likes and comments from Instagram'
                      : 'No Instagram post id saved for this content item'
                  }
                >
                  {refreshingInstagramMetrics ? 'Refreshing…' : 'Refresh Instagram Stats'}
                </Button>
              </div>

              <div className="cdw-instagram-metrics__grid">
                <div>
                  <p className="cdw-meta-item__label">Post ID</p>
                  <p className="cdw-meta-item__value">
                    {contentItem.instagram_post_id || 'Not available'}
                  </p>
                </div>
                <div>
                  <p className="cdw-meta-item__label">Likes</p>
                  <p className="cdw-meta-item__value">{contentItem.engagement_likes ?? 0}</p>
                </div>
                <div>
                  <p className="cdw-meta-item__label">Comments</p>
                  <p className="cdw-meta-item__value">{contentItem.engagement_comments ?? 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Publish date — displayed here, editable in Edit tab */}
          <div className="cdw-publish-date-row">
            <div className="cdw-publish-date-row__info">
              <p className="cdw-meta-item__label">Publish date</p>
              <div className="cdw-publish-date-row__display">
                <p className="cdw-meta-item__value">
                  {outline.publish_at ? `📅 ${formatDate(outline.publish_at)}` : '—'}
                </p>
              </div>
              <p className="cdw-publish-date-row__warning" style={{ marginTop: 'var(--space-2)' }}>
                Edit this in the <strong>Edit</strong> tab under <strong>Post Setup</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduler tasks */}
      {schedulerTasks && schedulerTasks.length > 0 && (
        <div className="cdw-meta-card">
          <div className="cdw-meta-card__header">Pipeline Tasks</div>
          <div className="cdw-meta-card__body">
            <div className="cdw-task-list">
              {schedulerTasks.map((task) => (
                <div key={task.id} className="cdw-task-row">
                  <span className="cdw-task-row__type">
                    {taskTypeLabel[task.task_type] ?? task.task_type_display}
                  </span>
                  <span className="cdw-task-row__date">
                    {new Date(task.scheduled_for).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                  <Badge variant={taskStatusVariant(task.status)} dot>
                    {task.status_display ?? task.status}
                  </Badge>
                  {task.last_error && (
                    <span
                      className="cdw-task-row__error"
                      title={task.last_error}
                      aria-label="Task error"
                    >
                      ⚠️
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="cdw-meta-card">
        <div className="cdw-meta-card__header">Workflow Timeline</div>
        <div className="cdw-meta-card__body">
          <div className="cdw-timeline">
            {stepsWithActive.map((step, i) => (
              <div key={i} className="cdw-timeline-item">
                <div
                  className={[
                    'cdw-timeline-dot',
                    step.done ? 'cdw-timeline-dot--done' : '',
                    step.active ? 'cdw-timeline-dot--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
                <div className="cdw-timeline-content">
                  <div
                    className="cdw-timeline-content__label"
                    style={{
                      color: step.done
                        ? 'var(--color-neutral-800)'
                        : step.active
                          ? 'var(--color-primary-600)'
                          : 'var(--color-neutral-400)',
                    }}
                  >
                    {step.label}
                  </div>
                  {step.date && <div className="cdw-timeline-content__date">{step.date}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="cdw-danger-zone">
        <p className="cdw-danger-zone__title">Danger Zone</p>
        {confirmDelete ? (
          <>
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-neutral-700)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Are you sure? This will permanently delete the outline and all its content.
            </p>
            <div className="cdw-danger-zone__actions">
              <Button variant="danger" size="sm" onClick={onDelete}>
                Yes, delete permanently
              </Button>
              <Button variant="secondary" size="sm" onClick={onCancelDelete}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="cdw-danger-zone__actions">
            <Button variant="danger" size="sm" onClick={onConfirmDelete}>
              Delete Outline
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function ContentDetailWorkspace(props: ContentDetailWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<Tab>(props.initialTab ?? 'preview');

  useEffect(() => {
    setActiveTab(props.initialTab ?? 'preview');
  }, [props.initialTab]);

  return (
    <div>
      {/* Tab bar */}
      <div className="cdw-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`cdw-tab${activeTab === tab.id ? ' cdw-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Preview panel */}
      <div
        className={`cdw-panel${activeTab === 'preview' ? ' cdw-panel--active' : ''}`}
        role="tabpanel"
        aria-label="Preview"
      >
        <PreviewTab
          outline={props.outline}
          contentItem={props.contentItem}
          currentMediaIndex={props.currentMediaIndex}
          onPrevMedia={props.onPrevMedia}
          onNextMedia={props.onNextMedia}
          onSetMediaIndex={props.onSetMediaIndex}
          generatingContent={props.generatingContent}
          generatingMediaIds={props.generatingMediaIds}
          multiSlideJobInFlight={props.multiSlideJobInFlight}
          regeneratingAllMedia={props.regeneratingAllMedia}
          onGenerateContent={props.onGenerateContent}
          onRegenerateContent={props.onRegenerateContent}
          onGenerateMedia={props.onGenerateMedia}
          onApprove={props.onApprove}
          onApproveContentItem={props.onApproveContentItem}
          onOpenPublishModal={props.onOpenPublishModal}
          onToggleHideMedia={props.onToggleHideMedia}
          onUploadMediaFile={props.onUploadMediaFile}
          workspace={props.workspace}
          onToggleCtaImage={props.onToggleCtaImage}
          togglingCta={props.togglingCta}
          onGoToCtaSettings={props.onGoToCtaSettings}
        />
      </div>

      {/* Edit panel */}
      <div
        className={`cdw-panel${activeTab === 'edit' ? ' cdw-panel--active' : ''}`}
        role="tabpanel"
        aria-label="Edit"
      >
        <EditTab
          outline={props.outline}
          contentItem={props.contentItem}
          allowedFormats={props.allowedFormats}
          generatingContent={props.generatingContent}
          generatingMediaIds={props.generatingMediaIds}
          multiSlideJobInFlight={props.multiSlideJobInFlight}
          regeneratingAllMedia={props.regeneratingAllMedia}
          onGenerateContent={props.onGenerateContent}
          onRegenerateContent={props.onRegenerateContent}
          onGenerateMedia={props.onGenerateMedia}
          editingCaption={props.editingCaption}
          captionDraft={props.captionDraft}
          savingCaption={props.savingCaption}
          onStartEditCaption={props.onStartEditCaption}
          onCaptionDraftChange={props.onCaptionDraftChange}
          onSaveCaption={props.onSaveCaption}
          onCancelEditCaption={props.onCancelEditCaption}
          editingHashtags={props.editingHashtags}
          hashtagsDraft={props.hashtagsDraft}
          savingHashtags={props.savingHashtags}
          onStartEditHashtags={props.onStartEditHashtags}
          onHashtagsDraftChange={props.onHashtagsDraftChange}
          onSaveHashtags={props.onSaveHashtags}
          onCancelEditHashtags={props.onCancelEditHashtags}
          editingPromptId={props.editingPromptId}
          promptDraft={props.promptDraft}
          savingPrompt={props.savingPrompt}
          onStartEditPrompt={props.onStartEditPrompt}
          onPromptDraftChange={props.onPromptDraftChange}
          onSavePrompt={props.onSavePrompt}
          onCancelEditPrompt={props.onCancelEditPrompt}
          onDeleteMedia={props.onDeleteMedia}
          onAddMediaPrompt={props.onAddMediaPrompt}
          onReorderMedia={props.onReorderMedia}
          onUploadMediaFile={props.onUploadMediaFile}
          onToggleHideMedia={props.onToggleHideMedia}
          savingOutlineMeta={props.savingOutlineMeta}
          onSaveOutlineMeta={props.onSaveOutlineMeta}
        />
      </div>

      {/* Meta panel */}
      <div
        className={`cdw-panel${activeTab === 'meta' ? ' cdw-panel--active' : ''}`}
        role="tabpanel"
        aria-label="Meta & Workflow"
      >
        <MetaTab
          outline={props.outline}
          contentItem={props.contentItem}
          confirmDelete={props.confirmDelete}
          onConfirmDelete={props.onConfirmDelete}
          onCancelDelete={props.onCancelDelete}
          onDelete={props.onDelete}
          formatDate={props.formatDate}
          getPillarName={props.getPillarName}
          getPlanName={props.getPlanName}
          schedulerTasks={props.schedulerTasks}
          refreshingInstagramMetrics={props.refreshingInstagramMetrics}
          onRefreshInstagramMetrics={props.onRefreshInstagramMetrics}
        />
      </div>
    </div>
  );
}
